import { Component, OnInit, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DropdownModule } from 'primeng/dropdown';
import { LocationProducts, NetsapiensSite, Product } from '@voice/utils';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from 'primeng/button';
import { CurrencyPipe, NgIf } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import {
  formatCurrency,
  parseRateInput,
  isValidProductForm,
  ProductFormData,
} from './utils/product-form.utils';

interface ProductFormDialogData {
  record?: { location?: string };
  sites?: NetsapiensSite[];
  products?: Product[];
  selectedProduct?: Product;
}

@Component({
  selector: 'app-client-onboarding-product-form',
  standalone: true,
  imports: [
    DropdownModule,
    FormsModule,
    ButtonDirective,
    CurrencyPipe,
    NgIf,
    InputTextModule,
    CalendarModule,
  ],
  templateUrl: './product-form.component.html',
})
export class ProductFormComponent implements OnInit {
  private readonly dialogConfig = inject<DynamicDialogConfig<ProductFormDialogData>>(
    DynamicDialogConfig
  );
  private readonly dialogRef = inject(DynamicDialogRef);

  location?: string;
  sites: NetsapiensSite[] = [];
  products: Product[] = [];
  selectedProduct?: Product;
  quantity = 1;
  rate?: number;
  catalogRate?: number;
  description?: string;
  effectiveDate?: Date;

  readonly formatCurrency = formatCurrency;

  ngOnInit(): void {
    this.initializeFormData();
    this.setupPreSelectedProduct();
  }

  onProductChange(product: Product): void {
    this.setupProduct(product);
  }

  onRateChange(value: unknown): void {
    this.rate = parseRateInput(value);
  }

  save(): void {
    if (!isValidProductForm(this.getFormData())) {
      return;
    }

    const product = this.buildProduct();
    const result: LocationProducts = {
      location: this.location!,
      products: [product],
    };
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private initializeFormData(): void {
    const data = this.dialogConfig.data;
    this.location = data?.record?.location;
    this.sites = data?.sites || [];
    this.products = data?.products || [];
  }

  private setupPreSelectedProduct(): void {
    const selectedProduct = this.dialogConfig.data?.selectedProduct;
    if (selectedProduct) {
      this.setupProduct(selectedProduct);
    }
  }

  private setupProduct(product: Product): void {
    this.selectedProduct = product;
    this.description = product.description;
    this.catalogRate = product.rate;
    this.rate = product.rate;
    this.quantity = 1;
    this.effectiveDate = undefined;
  }

  private buildProduct(): Product {
    if (!this.selectedProduct || !this.location) {
      throw new Error('Invalid form state: missing required fields');
    }

    return {
      id: this.selectedProduct.id,
      code: this.selectedProduct.code,
      description: this.description || this.selectedProduct.description,
      rate: this.rate ?? this.catalogRate,
      quantity: this.quantity,
      effectiveDate: this.effectiveDate,
    };
  }

  private getFormData(): ProductFormData {
    return {
      location: this.location,
      selectedProduct: this.selectedProduct,
      quantity: this.quantity,
      rate: this.rate,
      catalogRate: this.catalogRate,
      description: this.description,
      effectiveDate: this.effectiveDate,
    };
  }
}
